import { useRef, useState } from "react";
import { Camera, Upload as UploadIcon } from "lucide-react";
import Avatar from "./Avatar";
import CameraCaptureModal from "./CameraCaptureModal";
import ImageCropModal from "./ImageCropModal";

// Ported from frontend_Admin's PhotoPicker (pages/Users.jsx) — same
// FileReader→base64-data-URL technique, the same shared CameraCaptureModal
// pattern, and the same ImageCropModal step before ever calling onChange.
// Controlled: `photo` is the current data URL (or null/""), `onChange`
// receives the new one (or "" to remove). The final value is exactly what
// gets sent as `photo` in the registration payload / PUT /auth/me/photo —
// this component never talks to the network itself.
const MAX_FILE_BYTES = 8 * 1024 * 1024; // friendly client-side pre-check; the server's own 20MB data-URL cap (auth.process_avatar_photo) is the real backstop

// `collapsed` switches to the Account-page layout: a centered avatar with
// a single "Change Photo" trigger underneath, which reveals the same
// Upload/Take Photo row on click instead of always showing it inline
// (Register's own layout — the default, `collapsed=false` — is unchanged).
export default function PhotoPicker({ name, photo, onChange, disabled = false, collapsed = false, label = "Photo" }) {
  const fileRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropSource, setCropSource] = useState(null); // "camera" | "upload" — decides what Cancel-from-crop does next
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(!collapsed);

  function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!/^image\/(png|jpeg|jpg|webp)$/.test(file.type)) {
      setError("Please choose a PNG, JPEG, or WEBP image.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("Image is too large. Please choose a photo under 8MB.");
      return;
    }
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = () => {
      setProcessing(false);
      setCropSource("upload");
      setCropSrc(reader.result);
    };
    reader.onerror = () => {
      setProcessing(false);
      setError("Unable to read that file. Please try a different image.");
    };
    reader.readAsDataURL(file);
  }

  const actionButtons = (
    <div className="flex gap-2">
      <button
        type="button"
        disabled={disabled || processing}
        onClick={() => fileRef.current?.click()}
        className="flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
      >
        <UploadIcon size={13} />
        {processing ? "Processing..." : photo ? "Replace" : "Upload"}
      </button>
      <button
        type="button"
        disabled={disabled || processing}
        onClick={() => setShowCamera(true)}
        className="flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
      >
        <Camera size={13} />
        Take Photo
      </button>
    </div>
  );

  return (
    <div>
      {!collapsed && (
        <label className="mb-2 block text-sm font-medium text-neutral-700">
          {label} <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
      )}
      <div className={collapsed ? "flex flex-col items-center gap-2.5" : "flex items-center gap-4"}>
        <Avatar name={name} photo={photo} size={collapsed ? "h-24 w-24" : "h-16 w-16"} textSize={collapsed ? "text-2xl" : "text-lg"} />
        {collapsed ? (
          expanded ? (
            <div className="flex flex-col items-center gap-1.5">
              {actionButtons}
              {photo && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange("")}
                  className="text-[11px] font-semibold text-red-500 hover:text-red-600 disabled:opacity-50"
                >
                  ✕ Remove photo
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setExpanded(true)}
              className="rounded-lg border border-neutral-300 px-3.5 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Change Photo
            </button>
          )
        ) : (
          <div className="flex flex-col items-start gap-1.5">
            {actionButtons}
            {photo && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange("")}
                className="text-[11px] font-semibold text-red-500 hover:text-red-600 disabled:opacity-50"
              >
                ✕ Remove photo
              </button>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={pick}
      />
      {showCamera && (
        <CameraCaptureModal
          onCapture={(dataUrl) => {
            setShowCamera(false);
            setCropSource("camera");
            setCropSrc(dataUrl);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          onCancel={() => {
            setCropSrc(null);
            // Cancelling a crop that came from the camera reopens the
            // camera instead of just closing — matches the "retake photo"
            // state the task asks for, with no separate button needed.
            if (cropSource === "camera") setShowCamera(true);
          }}
          onConfirm={(croppedDataUrl) => {
            setCropSrc(null);
            setError("");
            onChange(croppedDataUrl);
          }}
        />
      )}
    </div>
  );
}
